import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Stepper from '@mui/material/Stepper'
import { ChevronLeftRounded } from '@mui/icons-material'

import { useStores } from '@trivie/core'
import { Button } from '@trivie/ui-web/components/button'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { SubscriptionProduct } from '@trivie/core/src/models/subscriptions/SubscriptionProduct'

import { SubscriptionDetails } from './subscription-details'
import { SubscriptionContent } from './subscription-content'
import { SubscriptionTags } from './subscription-tags'
import { SubscriptionReview } from './subscription-review'

interface SubscriptionCreateDialogProps {
  open: boolean
  requestClose: () => void
  editingProduct?: any
}

const steps = ['Details', 'Content', 'Tags', 'Review']

export const SubscriptionCreateDialog = observer((props: SubscriptionCreateDialogProps) => {
  const { open, requestClose, editingProduct } = props
  const { subscriptionStore } = useStores()

  const isEditMode = Boolean(editingProduct)

  const [activeStep, setActiveStep] = useState(0)
  const [form, setForm] = useState(() => SubscriptionProduct.create())

  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setActiveStep(0)
      const existingSkus = subscriptionStore.products
        .filter((p) => !editingProduct || p.id !== editingProduct.id)
        .map((p) => p.sku)
      if (editingProduct) {
        const newForm = SubscriptionProduct.create({
          id: editingProduct.id,
          title: editingProduct.title || '',
          description: editingProduct.description || '',
          sku: editingProduct.sku || '',
          duration_days: editingProduct.duration_days ?? null,
          is_active: editingProduct.is_active ?? true,
          color: editingProduct.color,
          change_applies_to: 'all',
        })
        newForm.setExistingSkus(existingSkus)
        setForm(newForm)
      } else {
        const newForm = SubscriptionProduct.create()
        newForm.setExistingSkus(existingSkus)
        setForm(newForm)
      }
    }
  }, [open])

  const stepIsValid = () => {
    switch (activeStep) {
      case 0:
        return !form.errors.title && !form.errors.sku && !form.errors.duration_days
      case 1:
        return form.selectedContent.length > 0
      case 2:
        return true
      case 3:
        return true
      default:
        return false
    }
  }

  const handleNext = async () => {
    if (activeStep === 3) {
      let success: any
      if (isEditMode) {
        success = await subscriptionStore.updateProduct(editingProduct.id, form.payload)
      } else {
        success = await subscriptionStore.createProduct(form.payload)
      }
      if (!success) {
        setError(
          isEditMode
            ? 'There was an issue updating this subscription'
            : 'There was an issue creating this subscription',
        )
      } else {
        subscriptionStore.getProducts()
        if (isEditMode) {
          subscriptionStore.getProduct(editingProduct.id)
        }
      }
      setActiveStep(4)
    } else if (activeStep === 4) {
      if (error) {
        setActiveStep(3)
        setError('')
      } else {
        setActiveStep(0)
        requestClose()
      }
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <SubscriptionDetails form={form} />
      case 1:
        return (
          <SubscriptionContent
            form={form}
            isEditMode={isEditMode}
            editingProduct={editingProduct}
          />
        )
      case 2:
        return <SubscriptionTags form={form} editingProduct={editingProduct} />
      case 3:
        return <SubscriptionReview form={form} isEditMode={isEditMode} />
      default:
        return null
    }
  }

  const dialogTitle = error ? 'Error' : isEditMode ? 'Edit Subscription' : 'Create Subscription'
  const confirmLabel = isEditMode ? 'Save' : 'Create'
  const successVerb = isEditMode ? 'updated' : 'created'

  return (
    <div>
      <Dialog
        open={activeStep === 4}
        title={dialogTitle}
        danger={Boolean(error)}
        onConfirm={handleNext}
        dialogText={error || `The product "${form.title}" was successfully ${successVerb}!`}
        onClose={() => {
          setActiveStep(0)
          setError('')
          requestClose()
        }}
        maxWidth="xs"
        confirmBtnLabel={error ? 'Back' : 'Close'}
      />
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="md"
        onClose={() => {
          setActiveStep(0)
          requestClose()
        }}
        sx={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        title={dialogTitle}
        open={open && activeStep !== 4}
        actions={
          <div
            style={{
              display: 'flex',
              flex: 1,
              justifyContent: activeStep === 0 ? 'flex-end' : 'space-between',
            }}
          >
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ChevronLeftRounded />}
                text="Back"
                variant="text"
              />
            )}
            <Button
              onClick={handleNext}
              disabled={!stepIsValid()}
              text={activeStep === 3 ? confirmLabel : 'Next'}
            />
          </div>
        }
      >
        <Stepper
          style={{ paddingLeft: 0, paddingRight: 0, marginBottom: 32 }}
          nonLinear
          activeStep={activeStep}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepButton disableRipple disabled>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: activeStep === 2 ? 'visible' : 'hidden',
            ...(activeStep === 1 ? { height: 514 } : {}),
            ...(activeStep === 2 ? { minHeight: 514 } : {}),
          }}
        >
          {getStepContent(activeStep)}
        </div>
      </Dialog>
    </div>
  )
})
